import React, { useState } from 'react';
import './NewCategoria.css'; // Importamos los estilos específicos
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTimes,
    faCheck,
    faTag,
    faFolder
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';

// Sistema de notificaciones usando las clases GLOBALES del CSS
const Toast = ({ toasts }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="product-toast-container">
      {toasts.map(toast => (
        <div key={toast.id} className={`product-toast ${toast.type}`}>
          <div className="toast-icon">
            {toast.type === 'success' && '✅'}
            {toast.type === 'error' && '❌'}
            {toast.type === 'warning' && '⚠️'}
            {toast.type === 'info' && 'ℹ️'}
          </div>
          <div className="toast-content">
            <div className="toast-title">{toast.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Crea una categoría.
 *
 * Props (todas opcionales; sin ellas se comporta como el botón standalone):
 *  - onCreated(cat): callback tras crear con { idCategoria, categoria }. Si se
 *    provee, NO recarga la página (ideal para embeber en otro formulario).
 *  - buttonLabel: texto del botón disparador (default 'Nueva Categoría').
 *  - buttonClassName / buttonStyle / buttonTitle: personalización del disparador.
 *  - embedded: eleva el z-index del modal para que quede sobre otro modal.
 */
export default function NewCategoria({
    onCreated,
    buttonLabel = 'Nueva Categoría',
    buttonClassName = 'btn-product-primary flex-center',
    buttonStyle,
    buttonTitle,
    embedded = false,
}) {
    const [mensaje, setMensaje] = useState('');
    const [categoria, setCategoria] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [toasts, setToasts] = useState([]);

    // Sistema de notificaciones
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        const newToast = { id, message, type };
        setToasts(prev => [...prev, newToast]);

        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 4000);
    };

    const toggleModal = () => {
        setCategoria('');
        setMensaje('');
        setModalOpen(!modalOpen);
    };

    const validarFormulario = () => {
        if (!categoria.trim()) {
            showToast('Ingrese el nombre de la categoría', 'error');
            return false;
        }
        if (categoria.length < 2) {
            showToast('La categoría debe tener al menos 2 caracteres', 'error');
            return false;
        }
        if (categoria.length > 50) {
            showToast('La categoría no puede exceder 50 caracteres', 'warning');
            return false;
        }
        return true;
    };

    const crear = async () => {
        if (!validarFormulario()) return;

        const data = { categoria: categoria.trim() };

        setMensaje('Procesando...');

        try {
            const response = await fetch(`${baseURL}/categoriasPost.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (result.mensaje) {
                setMensaje('');
                showToast(result.mensaje, 'success');
                toggleModal();

                if (typeof onCreated === 'function') {
                    // Modo embebido: informar al padre para que refresque y seleccione.
                    onCreated({
                        idCategoria: result.idCategoria,
                        categoria: result.categoria,
                    });
                } else {
                    // Modo standalone: recargar para refrescar el listado.
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                }
            } else if (result.error) {
                setMensaje('');
                showToast(result.error, 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            setMensaje('');
            showToast('Error de conexión. Inténtelo nuevamente.', 'error');
        }
    };

    // Función para obtener clase del contador de caracteres
    const getCharacterCounterClass = (current, max) => {
        if (current >= max * 0.9) return 'character-counter error';
        if (current >= max * 0.7) return 'character-counter warning';
        return 'character-counter';
    };

    return (
        <div className='NewContain' style={embedded ? { display: 'inline-flex' } : undefined}>
            <Toast toasts={toasts} />

            {/* Botón disparador (personalizable para embeber) */}
            <button
                type='button'
                onClick={toggleModal}
                className={buttonClassName}
                style={buttonStyle}
                title={buttonTitle}
            >
                <FontAwesomeIcon icon={faPlus} size="sm" />
                {buttonLabel ? <span>{buttonLabel}</span> : null}
            </button>

            {modalOpen && (
                <div className='product-modal-backdrop' style={embedded ? { zIndex: 1300 } : undefined}>
                    <div className='product-modal-container' style={{ maxWidth: '560px' }}>
                        {/* Header usando clases globales */}
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faFolder} size="lg" />
                                Crear Nueva Categoría
                            </div>
                            <button className='product-close-button' onClick={toggleModal}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        {/* Body usando clases globales */}
                        <div className='product-modal-body'>
                            {/* Sección de Categoría Principal */}
                            <div className="product-section-card">
                                <div className="product-section-header">
                                    <div className="product-section-title">
                                        <FontAwesomeIcon icon={faFolder} />
                                        Categoría Principal
                                    </div>
                                </div>

                                <div className="product-section-content">
                                    <div className='product-form-group'>
                                        <label className='product-form-label'>
                                            <FontAwesomeIcon icon={faTag} size="sm" />
                                            Nombre de la Categoría *
                                        </label>
                                        <input
                                            type='text'
                                            className={`product-form-input ${categoria.length > 40 ? 'input-error' : categoria.length > 10 ? 'input-success' : ''}`}
                                            value={categoria}
                                            onChange={(e) => setCategoria(e.target.value)}
                                            placeholder='Ingrese el nombre de la categoría'
                                            maxLength={50}
                                            autoFocus
                                            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); crear(); } }}
                                        />
                                        <div className={getCharacterCounterClass(categoria.length, 50)}>
                                            {categoria.length}/50 caracteres
                                        </div>
                                        {categoria.length > 0 && categoria.length < 2 && (
                                            <div className="form-hint error">
                                                Mínimo 2 caracteres requeridos
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer usando clases globales */}
                        <div className='product-submit-section'>
                            <button
                                type='button'
                                onClick={toggleModal}
                                className='btn-product-secondary'
                            >
                                Cancelar
                            </button>
                            {mensaje ? (
                                <button
                                    type='button'
                                    className='btn-product-primary btn-loading-custom'
                                    disabled
                                >
                                    Procesando...
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={crear}
                                    className='btn-product-primary flex-center'
                                    disabled={!categoria.trim()}
                                    style={{ gap: '8px' }}
                                >
                                    <FontAwesomeIcon icon={faCheck} size="sm" />
                                    Crear Categoría
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
